import { GridPage } from "../../shared/GridPage";
import React, {useEffect, useRef, useState} from "react";
import { ColumnConfig, GridConfig } from "../../shared/Grid";
import { PagingInfo, SortState } from "../../../../utils/Grid";
import { Link } from "react-router-dom";
import {
    useDeleteControlListValueMutation,
    useGetControlListActionsQuery, useGetControlListGroupsQuery,
    useGetControlListReasonsQuery,
} from "../../../../app/apiSlice";
import {useAppDispatch, useAppSelector, useClaims, useDebounce} from "../../../../app/hooks";
import { Consumer } from "../../customer/Consumer";
import {ControlListValue, ControlListValueFetchSearchCriteria} from "../ControlListValue";
import {
    canFetchMoreControlListValues,
    fetchControlListValues,
    fetchMoreControlListValues,
    isFetchingControlListValues,
    refetchAllControlListValues,
    resetControlListValueCriteria,
    saveControlListValueCriteria,
    selectActiveControlListGroup, selectAllControlListActions, selectAllControlListGroups, selectAllControlListReasons,
    selectControlListValues,
    selectSavedControlListCriteria, STORAGE_KEY_CONTROL_LIST_CRITERIA
} from "../ControlListValueSlice";
import { ControlListValueEdit, MatchingCriteria } from "../edit/ControlListValueEdit";
import { Button } from "reactstrap";
import { getPrettyAddress, Utils } from "../../../../utils/Utils";
import { Address } from "../../../input/address/Address";
import { closeModal, showConfirmationModal, showInformationModal } from "../../../modal/ModalSlice";
import { ModalType } from "../../../modal/ModalType";
import { ADMIN, CS3, TEAM_LEAD } from "../../../../utils/Roles";
import { ControlListValueType } from "../ControlListGroup";
import {usePersistedSelector} from "../../../../persistence";

import {GridCriteriaPanelV2} from "../../shared/GridCriteriaPanelV2";

const ConsumerCellDisplay = ( {val}: {val: Consumer}) => { return <Link to={`/admin/consumer/${(val)?.id}`}>{(val)?.email}</Link>};

export const ControlListBrowse = () => {
    
    useGetControlListGroupsQuery();
    useGetControlListActionsQuery();
    useGetControlListReasonsQuery();
// Routing hooks
    const { hasPermission } = useClaims();

// Hooks with no parameters
    const [deleteControlListValue] = useDeleteControlListValueMutation();

// Selectors
    const controlListValues = useAppSelector(selectControlListValues);
    const moreAvailable = useAppSelector(canFetchMoreControlListValues);
    const isLoading = useAppSelector(isFetchingControlListValues);
    const fetchStatus = useAppSelector(state => state.controlListValues.status);
    const selectedGroup = useAppSelector(selectActiveControlListGroup)
    const controlListGroups = useAppSelector(selectAllControlListGroups);
    const controlListActions = useAppSelector(selectAllControlListActions);
    const controlListReasons = useAppSelector(selectAllControlListReasons);
    
// States
    const criteria = usePersistedSelector(
        STORAGE_KEY_CONTROL_LIST_CRITERIA,
        selectSavedControlListCriteria
    );
    const [search, setSearch] = useState('');
    const [sort, setSort] = useState<SortState<ControlListValue>>({
        column: 'stringValueOne',
        serverColumn: 'stringValueOne',
        direction: 'asc'
    });
    const [focusedControlListValue, setFocusedControlListValue] = useState<ControlListValue>();
    const [showEditModal, setShowEditModal] = useState(false);

// Debounced states
    const debouncedSearch = useDebounce(search, 500);

// Other hooks
    const dispatch = useAppDispatch();

// Effects
// Initial pull of data and on sort/search change
    const isInitialSearchRef = useRef(true);
    useEffect(() => {
        if (
            isInitialSearchRef.current &&
            criteria.controlListGroupId &&
            criteria.controlListActionIds &&
            criteria.controlListReasonIds
        ) {
            isInitialSearchRef.current = false;
            executeSearch(criteria)
        }
    }, [sort, debouncedSearch, criteria, dispatch]);

// Clear focused control list value on modal close
    useEffect(() => {
        if (!showEditModal) {
            setFocusedControlListValue(undefined);
        }
    }, [showEditModal]);

// Helper functions
    const toggleEditModal = (controlListValue?: ControlListValue) => {
        setFocusedControlListValue(controlListValue);
        setShowEditModal(old => !old);
    };

    const onValueSave = () => {
        dispatch(refetchAllControlListValues({ criteria }));
    };

    const fetchMore = (paging: PagingInfo) => {
        if (fetchStatus !== 'succeeded') return;
        const newCriteria = { ...criteria, search };
        dispatch(fetchMoreControlListValues({ criteria: newCriteria, paging }));
        setCriteria(newCriteria)
    };

    const onDeleteClick = (
        controlListValueId: number,
        event: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) => {
        event.stopPropagation();
        dispatch(showConfirmationModal({
            modalType: ModalType.Delete,
            onConfirm: () => confirmDeleteControlListValue(controlListValueId)
        }));
    };

    const confirmDeleteControlListValue = async (controlListValueId: number) => {
        try {
            if (controlListValueId) {
                await deleteControlListValue(controlListValueId).unwrap();
            }
            dispatch(closeModal());
            const newCriteria = { ...criteria, search };
            dispatch(refetchAllControlListValues({
                criteria: newCriteria,
                paging: {
                    sortKey: sort.serverColumn!,
                    sortDirection: sort.direction
                }
            }));
            setCriteria(newCriteria);
        } catch (e: any) {
            dispatch(showInformationModal({
                title: 'Could Not Delete Control List Value!',
                text: e.status === 401
                    ? 'Sorry, you do not have permission to do that.'
                    : 'Error deleting control list value. Please try again.'
            }));
        }
    };

    const getStringDisplayValue = (val?: string) => {
        if (!val) return '';
        try {
            const address: Address = JSON.parse(val);
            if (address.street) {
                return getPrettyAddress(address);
            }
            return val;
        } catch {
            return val;
        }
    };

    const getConsumerCellDisplay = (val: Consumer) => {
        return <ConsumerCellDisplay val={val} />;
    };

    const onResetCriteria = () => {
        dispatch(resetControlListValueCriteria())
    }
    
    const onCriteriaChange = (newCriteria: ControlListValueFetchSearchCriteria) => {
        setCriteria(newCriteria);
    }

    function executeSearch(
        newCriteria: ControlListValueFetchSearchCriteria = criteria,
        newSort: SortState<ControlListValue> = sort) {
        dispatch(fetchControlListValues({
            criteria: newCriteria,
            paging: {
                sortKey: newSort.serverColumn!,
                sortDirection: newSort.direction
            }
        }));
    }

    function setCriteria(newCriteria: ControlListValueFetchSearchCriteria) {
        dispatch(saveControlListValueCriteria(newCriteria));
    }

    const onSearchClick = (newCriteria: ControlListValueFetchSearchCriteria) => {
        setCriteria(newCriteria);
        executeSearch(newCriteria);
    }
    
    const onSortChange = (newSort: SortState<ControlListValue>) => {
        setSort(newSort);
        executeSearch(criteria, newSort);
    }
    
// Other variables
    const hasBasicEditPermission = hasPermission([CS3, TEAM_LEAD, ADMIN]);
    const valueOneLabel = selectedGroup?.stringValueOneLabel ?? 'Value One';
    const valueTwoLabel = selectedGroup?.stringValueTwoLabel ?? 'Value Two';
    const usesValueTwo = selectedGroup?.valueTwoType !== ControlListValueType.None;
    const usesConsumerValueOne = selectedGroup?.valueOneType === ControlListValueType.UserId;

// Config
    const config: GridConfig<ControlListValue> = {
        configSections: [
            {
                columns: [
                    new ColumnConfig({
                        field: 'controlListGroupId',
                        display: 'Group',
                        isHidden: true,
                        type: 'select',
                        choices: controlListGroups?.map(clg => ({ value: clg.id, display: clg.name })) ?? []
                    }),
                    new ColumnConfig({
                        field: 'controlListActionIds',
                        display: 'Action',
                        isHidden: true,
                        type: 'multi-select',
                        choices: controlListActions?.map(cla => ({ value: cla.id, display: cla.name })) ?? []
                    }),
                    new ColumnConfig({
                        field: 'controlListAction.name',
                        display: 'Action',
                        isSearchCriteria: false,
                    }),
                    new ColumnConfig({
                        field: 'controlListReason.name',
                        display: 'Reason',
                        isSearchCriteria: false,
                    }),
                    ...(usesConsumerValueOne ?
                        [new ColumnConfig<ControlListValue>({
                            field: 'consumer',
                            display: 'Consumer',
                            isSearchCriteria: false,
                            cellDisplay: val => val ? getConsumerCellDisplay(val as Consumer) : 'No val',
                        })] :
                        [new ColumnConfig<ControlListValue>({
                            field: 'stringValueOne',
                            display: valueOneLabel,
                            cellDisplay: val => val ? getStringDisplayValue(val as string) : '',
                        })]),
                    ...(usesValueTwo ? [new ColumnConfig<ControlListValue>({
                        field: 'stringValueTwo',
                        display: valueTwoLabel,
                        cellDisplay: val => val ? getStringDisplayValue(val as string) : '',
                    })] : []),
                    new ColumnConfig({
                        field: 'description',
                        display: 'Description',
                    }),
                    new ColumnConfig({
                        field: 'controlListReasonIds',
                        display: 'Reason',
                        isHidden: true,
                        type: 'multi-select',
                        choices: controlListReasons?.map(clr => ({ value: clr.id, display: clr.name })) ?? []
                    }),
                    new ColumnConfig({
                        field: 'controlListGroup.name',
                        display: 'Group',
                        isSearchCriteria: false,
                    }),
                    ...(hasBasicEditPermission ? [new ColumnConfig<ControlListValue>({
                        field: 'id',
                        display: 'Action',
                        cellDisplay: val => val && Utils.isValidNumber(val)
                            ? <Button
                                color="danger"
                                className="btn btn-primary m-auto"
                                onClick={(e) => onDeleteClick(val, e)}
                            >
                                Delete
                            </Button>
                            : '',
                        isSortable: false,
                        isSearchCriteria: false,
                    })] : []),
                ]
            }
        ]
    };

    function handleFindMatchingClick(matchingCriteria: MatchingCriteria) {
        executeSearch(matchingCriteria);
    }

    return (
        <>
            <GridPage records={controlListValues ?? []}
                      gridConfig={config}
                      defaultCriteria={criteria}
                      title={'Control Lists'}
                      sort={sort}
                      onNewModalButtonClicked={hasBasicEditPermission ? toggleEditModal : undefined}
                      shouldShowCriteriaPanel={true}
                      onSortChanged={onSortChange}
                      onSearchChange={setSearch}
                      moreRecordsAvailable={moreAvailable}
                      onCriteriaChange={onCriteriaChange}
                      onShouldFetchMore={fetchMore}
                      onRowClick={hasBasicEditPermission ? toggleEditModal : undefined}
                      isLoading={isLoading}
                      onResetCriteria={onResetCriteria}
                      renderCriteriaPanel={({criteria: c, configSections}) =>
                          <GridCriteriaPanelV2 criteria={c}
                                               configSections={configSections}
                                               onResetCriteria={onResetCriteria}
                                               onSearchClick={onSearchClick}
                                               onCriteriaChange={onCriteriaChange}
                          />
                      }
            />
            
            
            { hasBasicEditPermission &&
                <ControlListValueEdit isOpen={showEditModal}
                                      onIsOpenChange={() => toggleEditModal()}
                                      onValueSaved={onValueSave}
                                      requestedControlListValue={focusedControlListValue}
                                      onFindMatchingClick={handleFindMatchingClick}
                />
            }
        </>
    )
}